document.addEventListener('alpine:init', () => {
  
  Alpine.store('remoteOrderForm', {
    allAvailableisInCart: false,
    variantId: null,
  });
  
  /*
    handle add to cart from pdp
    adds line attributes and dynamically renders the remote order notice
  */
  Alpine.data('remoteOrderForm', ({ productHandle }) => ({
    debug: false,
    enableRemoteOrderData: false,
    isRemoteOrder: false,
    remoteOrderNotice: '',
    remoteOrderAttribute: '',
    productHandle,
    currentVariantId: null,
    variantInventory: 0,

    init() {
      if (this.debug) console.log('⚠️ remoteOrder initted');
      
      this.setCurrentVariantOnLoad();
      this.setupListeners();
      
      // hack to force update the add to cart button when the remote order availability changes
      setTimeout(() => {
        this.$watch('$store.remoteOrderForm.allAvailableisInCart', (value) => {
          const variantPickerElement = document.querySelector('variant-picker');
          if (variantPickerElement) {
            variantPickerElement.updateAddToCartButton();
          }
        });
      }, 500);
    },
    
    setCurrentVariantOnLoad() {
      const urlParams = new URLSearchParams(window.location.search);
      const variantParam = urlParams.get('variant');
      
      if (variantParam) {
        this.currentVariantId = parseInt(variantParam);
        
        // set initial state if loaded directly
        this.handleVariantChange(this.currentVariantId);
      }
    },
    
    setupListeners() {
      document.addEventListener('on:variant:change', (event) => {
        this.currentVariantId = event.detail.variant ? event.detail.variant.id : null;
        this.handleVariantChange(this.currentVariantId);
      });
      
      this.observeCartFormChanges();
    },
    
    // as theme events are inconsistent, we need to observe the cart form for changes
    observeCartFormChanges() {
      const cartForm = document.querySelector('cart-form.cart-drawer');
      if (!cartForm) return;
      
      let debounceTimeout;
      const DEBOUNCE_MS = 300;

      const observer = new MutationObserver((mutationsList) => {
        if (mutationsList.some(mutation => mutation.type === 'childList' || mutation.type === 'subtree')) {
          clearTimeout(debounceTimeout);
          debounceTimeout = setTimeout(() => {
            this.handleVariantChange(this.currentVariantId);
          }, DEBOUNCE_MS);
        }
      });
      
      observer.observe(cartForm, { childList: true, subtree: true });
    },
    
    async handleVariantChange(variantId) {
      if (this.debug) console.log('⚠️ handleVariantChange', variantId);
      let url = `/products/${this.productHandle}/?section_id=api-pdp-remote-order`;
      
      if (variantId) {
        url += `&variant=${variantId}`;
      }
      
      if (this.debug) console.log('⚠️ url', url);
      
      try {
        const response = await fetch(url);
        const responseText = await response.text();
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        if (this.debug) console.log('⚠️ html', html);
        
        const json = JSON.parse(html.querySelector('script[type="application/json"]').textContent);
        if (this.debug) console.log('⚠️ json', json);
        
        this.enableRemoteOrderData = json.enableRemoteOrderData;
        this.isRemoteOrder = json.isRemoteOrder;        
        this.remoteOrderNotice = json.remoteOrderNotice;
        this.remoteOrderAttribute = json.remoteOrderAttribute;
        this.variantInventory = json.variantInventory;

        Alpine.store('remoteOrderForm').allAvailableisInCart = json.allAvailableisInCart;
        Alpine.store('remoteOrderForm').variantId = variantId;
      } catch (error) {
        console.error('⚠️ Error fetching remote order:', error);
        this.enableRemoteOrderData = false;
      }
    }
  }));

  /*
    handle cart merge
    ensures that the cart has the correct lines and quantities
  */
  Alpine.data('remoteOrderCartManager', ({ cartTimestamp, isCartPage = false, noticePropKey = 'OBS' }) => ({
    debug: false,
    handling: false,
    processedVariants: new Set(),
    skipNextCartMerge: false,
    cartTimestamp,
    noticePropKey,
    remoteOrderPropKey: '_system_remote_order',
    isCartPage,
    
    init() {
      if (this.debug) console.log('⚠️ remoteOrderCartManager initted');

      // Listen for cart drawer opening to check cart age and recalculate if needed
      document.addEventListener('on:cart-drawer:before-open', async (event) => {
        if (this.debug) console.log('⚠️ on:cart-drawer:before-open', this.cartTimestamp);
        this.handleCartTimestamp();
      });

      // theme cart updates falls into this
      document.addEventListener('on:cart:after-merge', (event) => {
        if (this.skipNextCartMerge) { // used to skip self-triggered cart merge to prevent infinite loop
          if (this.debug) console.log('⏩ Skipping remote order logic (self-triggered cart merge)');
          this.skipNextCartMerge = false;
          return;
        }
        
        if (this.debug) console.log('⚡ Running remote order logic due to on:cart:after-merge');
        this.handleRemoteOrderLines();
      });

      // API to manually trigger the remote order logic from outside
      document.addEventListener('dispatch:remote-order:handle', this.handleRemoteOrderLines.bind(this)); // not used, but ready
    },
    
    async handleCartTimestamp() {
      // Convert both timestamps to seconds
      const nowInSeconds = Math.floor(Date.now() / 1000);
      const cartTimestamp = parseInt(this.cartTimestamp || '0', 10);
      const oneDayInSeconds = 24 * 60 * 60;

      // If cart is older than 24 hours, recalculate and update timestamp
      if ((nowInSeconds - cartTimestamp) > oneDayInSeconds) {
        if (this.debug) console.log('⏳ Cart is older than 24h, recalculating remote order lines and updating timestamp');
        await this.handleRemoteOrderLines();
        try {
          await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              attributes: {
                cart_timestamp: nowInSeconds
              }
            })
          });
          if (this.debug) console.log('✅ cart_timestamp updated to', nowInSeconds);
        } catch (err) {
          console.error('⚠️ Failed to update cart_timestamp:', err);
        }
      }
    },
    
    async handleRemoteOrderLines() {
      if (this.debug) console.log('⚠️ handleRemoteOrderLines triggered');
      
      this.handling = true;
      this.processedVariants = new Set();
      document.dispatchEvent(new CustomEvent('on:remote-order:start-handle', { bubbles: true }));
      
      const cartLines = await this.getCartLines();
      if (this.debug) console.log('⚠️ cartLines', cartLines);
      if (!Array.isArray(cartLines)) return;

      for (const line of cartLines) {
        // skip if it has no remote order status
        if (!line.remote_order_enabled) continue;
        
        const variantId = line.variant_id;
        
        // for performance; we only need to process each variant once
        if (this.processedVariants.has(variantId)) continue;
        this.processedVariants.add(variantId);
        
        // this feature makes it normal to have multiple lines for the same variant due to mixed props
        const linesWithSameVariantId = cartLines.filter(item => item.variant_id === variantId);
        
        // accumulated inventory stock for the variant
        const accumulatedQuantity = linesWithSameVariantId.reduce((acc, item) => acc + item.quantity, 0);
        const inventoryQuantity = line.inventory_quantity;

        // existing lines for the variant that has remote order status set
        const linesWithStatus = linesWithSameVariantId.filter(item => {
          const remoteProp = item.properties.find(p => p.key === this.remoteOrderPropKey);
          // if no properties at all, handle it as if it has no status
          if (!Array.isArray(item.properties)) return false;
          if (!remoteProp) return false;
          
          // if it's not set to 'true' or there's no notice, also handle it as if it has no status
          if (remoteProp.value !== 'true') return false;
          const noticeProp = item.properties.find(p => p.key === this.noticePropKey && p.value && p.value.trim() !== '');
          if (!noticeProp) return false;
          
          return true;
        });
        
        // existing lines for the variant that does NOT have remote order status set
        const withStatusKeys = new Set(linesWithStatus.map(l => l.key));
        const linesWithoutStatus = linesWithSameVariantId.filter(item => !withStatusKeys.has(item.key));

        /* 👇 run through all cases and ensure correct states and quantity splits */

        // variant has no stock; ensure just one remote-enabled line
        if (inventoryQuantity < 1) {
          const needsFix =
            linesWithSameVariantId.length !== 1 ||
            linesWithStatus.length !== 1 ||
            linesWithStatus[0].quantity !== accumulatedQuantity;
          if (needsFix) {
            if (this.debug) console.log(`🚨 [${variantId}] inventory < 1: Fixing by removing all and re-adding correct line.`);
            for (const l of linesWithSameVariantId) {
              await this.removeLineItem(l.key);
            }
            await this.addLineItem(variantId, accumulatedQuantity, this.buildStatusProperties(line));
          } else if (this.debug) {
            console.log(`✅ [${variantId}] inventory < 1: Cart is already correct.`);
          }
        }
        
        // more quantity than stock; ensure correctly handled (remote status: all oversell, normal line: equal variant stock)
        else if (accumulatedQuantity > inventoryQuantity) {
          const oversellQty = accumulatedQuantity - inventoryQuantity;
          const needsFix =
            linesWithSameVariantId.length !== 2 ||
            linesWithoutStatus.length !== 1 ||
            linesWithStatus.length !== 1 ||
            linesWithoutStatus[0].quantity !== inventoryQuantity ||
            linesWithStatus[0].quantity !== oversellQty;
          if (needsFix) {
            if (this.debug) console.log(`🚨 [${variantId}] oversell: Fixing by removing all and re-adding correct lines.`);
            for (const l of linesWithSameVariantId) {
              await this.removeLineItem(l.key);
            }
            if (inventoryQuantity > 0) {
              await this.addLineItem(variantId, inventoryQuantity, this.buildNoStatusProperties(line));
            }
            if (oversellQty > 0) {
              await this.addLineItem(variantId, oversellQty, this.buildStatusProperties(line));
            }
          } else if (this.debug) {
            console.log(`✅ [${variantId}] oversell: Cart is already correct.`);
          }
        }
        
        // normal case; variant has enough stock to the accumulated quantity. 1 line with no status.
        else {
          const needsFix =
            linesWithSameVariantId.length !== 1 ||
            linesWithStatus.length !== 0 ||
            linesWithoutStatus[0].quantity !== accumulatedQuantity;
          if (needsFix) {
            if (this.debug) console.log(`🚨 [${variantId}] normal: Fixing by removing all and re-adding correct line.`);
            for (const l of linesWithSameVariantId) {
              await this.removeLineItem(l.key);
            }
            await this.addLineItem(variantId, accumulatedQuantity, this.buildNoStatusProperties(line));
          } else if (this.debug) {
            console.log(`✅ [${variantId}] normal: Cart is already correct.`);
          }
        }
      }
      
      this.handling = false;
      this.skipNextCartMerge = true;
      
      // theme event to update cart
      if (this.isCartPage) {
        window.location.reload();
      } else {
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
          bubbles: true,
          detail: { fromRemoteOrder: true }
        }));
      }
      
      document.dispatchEvent(new CustomEvent('on:remote-order:end-handle', { bubbles: true }));
    },
    
    /* 👇 Utilities and helpers */

    /**
     * Add a line item to the cart
     * @param {String | Number} variantId 
     * @param {Number} quantity 
     * @param {Object} properties 
     */
    async addLineItem(variantId, quantity, properties = {}) {
      if (this.debug) console.log('🛒 addLineItem', { variantId, quantity, properties });
      await fetch(window.theme.routes.cartAdd, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{ id: variantId, quantity, properties }]
        })
      });
    },
    
    /**
     * Update a line item in the cart
     * @param {String | Number} lineKey 
     * @param {Number} quantity 
     * @param {Object} properties 
     */
    async updateLineItem(lineKey, quantity, properties = undefined) {
      if (this.debug) console.log('🛒 updateLineItem', { lineKey, quantity, properties });
      
      const payload = { id: lineKey, quantity };
      
      if (properties) {
        payload.properties = properties;
      }
      
      await fetch(window.theme.routes.cartChange, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
    },
    
    /**
     * Remove a line item from the cart
     * @param {String | Number} lineKey 
     */
    async removeLineItem(lineKey) {
      if (this.debug) console.log('🛒 removeLineItem', { lineKey });
      await fetch(window.theme.routes.cartChange, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity: 0 })
      });
    },
    
    /**
     * Delete a line item and re-add it with new properties
     * @param {Object} line 
     * @param {Object} newProperties 
     */
    async deleteAndReAddLineItem(line, newProperties = {}) {
      if (this.debug) console.log('🛒 deleteAndReAddLineItem', { line, newProperties });
      await this.removeLineItem(line.key);
      await this.addLineItem(line.variant_id, line.quantity, newProperties);
    },

    /**
     * Check if a line has the remote order true property
     * @param {Object} line 
     * @returns {Boolean}
     */
    hasRemoteOrderTrue(line) {
      return (
        Array.isArray(line.properties) &&
        line.properties.some(
          (prop) => prop.key === this.remoteOrderPropKey && prop.value === 'true'
        )
      );
    },
    
    /**
     * Check if a line has the notice property
     * @param {Object} line 
     * @returns {Boolean}
     */
    hasNotice(line) {
      return (
        Array.isArray(line.properties) &&
        line.properties.some(
          (prop) => prop.key === this.noticePropKey && prop.value && prop.value.trim() !== ''
        )
      );
    },

    /**
     * Build properties for a line WITH status (remote order true + notice)
     * @param {Object} line 
     * @returns {Object}
     */
    buildStatusProperties(line) {
      const props = {};
      if (Array.isArray(line.properties)) {
        for (const prop of line.properties) {
          if (prop.key === this.noticePropKey) {
            props[prop.key] = prop.value;
          }
        }
      }

      // Always force true — this line is becoming the status/oversell line regardless
      // of what _system_remote_order was on whatever line it's derived from. Copying
      // the source value here (as this used to) let a stale 'false' survive onto a
      // line that also got a real notice, since '!"false"' is falsy in JS.
      props[this.remoteOrderPropKey] = 'true';
      if (!props[this.noticePropKey]) props[this.noticePropKey] = line.remote_notice;
      return props;
    },
    
    /**
     * Build properties for a line WITHOUT status (remote order false or no notice)
     * @param {Object} line 
     * @returns {Object}
     */
    buildNoStatusProperties(line) {
      const props = {};
      if (Array.isArray(line.properties)) {
        for (const prop of line.properties) {
          if (prop.key === this.remoteOrderPropKey) {
            props[prop.key] = 'false';
          }
        }
      }
      if (!props[this.remoteOrderPropKey]) props[this.remoteOrderPropKey] = 'false';
      return props;
    },
    
    /**
     * Fetch the current remote order line items
     * @returns {Array}
     */
    async getCartLines() {
      try {
        const response = await fetch('/cart?view=json');
        const responseText = await response.text();
        const json = JSON.parse(responseText);
        return json;
      } catch (error) {
        console.error('⚠️ Error fetching cart:', error);
        return null;
      }
    }
  }));
});